import React from 'react';
import { Navigate } from 'react-router-dom';
import { usePerspective } from '../contexts/PerspectiveContext';
import { logger } from '../utils/logger';

/**
 * RepRouteWrapper
 * Wraps rep routes to allow access for:
 * 1. Actual rep users (userType === 'rep' or 'client_rep' or 'CLIENT_REP')
 * 2. Admin users in switched rep view (perspective === 'REP')
 */
const RepRouteWrapper = ({ children, currentUser, isLoading = false }) => {
  const { isInSwitchedView, perspectiveContext, isLoading: perspectiveLoading } = usePerspective();

  // Normalize user type to uppercase for comparison
  const userType = currentUser?.userType?.toUpperCase();

  // Debug logging
  logger.log('RepRouteWrapper check:', {
    currentUser: currentUser ? { id: currentUser.id, userType: currentUser.userType } : null,
    userType,
    isLoading,
    perspectiveLoading,
    isInSwitchedView,
    currentPerspective: perspectiveContext?.currentPerspective,
    targetUserId: perspectiveContext?.targetUserId,
    targetUserType: perspectiveContext?.targetUserType,
  });

  // Show loading state while session is being restored
  if (isLoading) {
    logger.log('RepRouteWrapper: Session loading, showing placeholder');
    return <div style={{ padding: 16 }} />;
  }

  // If we have perspective state indicating switched view, wait for user to load
  if (isInSwitchedView && !currentUser) {
    logger.log('RepRouteWrapper: Perspective exists but no user yet, showing placeholder');
    return <div style={{ padding: 16 }} />;
  }

  // If perspective is still loading and we're an internal user, wait
  if (perspectiveLoading && userType === 'INTERNAL') {
    logger.log('RepRouteWrapper: Perspective loading for internal user, showing placeholder');
    return <div style={{ padding: 16 }} />;
  }

  // Allow if user is a rep (various type names)
  if (userType === 'REP' || userType === 'CLIENT_REP') {
    logger.log('RepRouteWrapper: Allowing access - user is REP/CLIENT_REP');
    return children;
  }

  // Allow if admin is in switched rep view
  const currentPerspective = perspectiveContext?.currentPerspective?.toUpperCase();
  const targetUserType = perspectiveContext?.targetUserType?.toUpperCase();
  
  if (
    userType === 'INTERNAL' &&
    isInSwitchedView &&
    (currentPerspective === 'REP' || targetUserType === 'REP' || targetUserType === 'CLIENT_REP')
  ) {
    logger.log('RepRouteWrapper: Allowing access - admin in switched REP view');
    return children;
  }

  // Otherwise redirect to appropriate location based on user type
  if (userType === 'INTERNAL') {
    logger.log('RepRouteWrapper: Internal user without rep perspective - redirecting to /org-select');
    return <Navigate to="/org-select" replace />;
  }

  logger.log('RepRouteWrapper: Denying access - redirecting to /');
  return <Navigate to="/" replace />;
};

export default RepRouteWrapper;

